import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Paper,
  Chip,
  Divider,
  Grid,
} from "@mui/material";
import {
  EmojiEvents as TrophyIcon,
  Bolt as FlashIcon,
  MusicNote as MusicIcon,
} from "@mui/icons-material";
import io from "socket.io-client";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:3001";

const stripAnswers = (question) => {
  if (!question || typeof question !== "object") return question;
  const copy = { ...question };
  const answerKeys = [
    "correct_answer",
    "correct_option",
    "correct_index",
    "correct_option_id",
    "is_correct",
    "answer",
    "correctAnswer",
  ];
  answerKeys.forEach((k) => delete copy[k]);
  if (Array.isArray(copy.options)) {
    copy.options = copy.options.map((opt) => {
      if (typeof opt === "string") return opt;
      if (!opt || typeof opt !== "object") return opt;
      const o = { ...opt };
      answerKeys.forEach((k) => delete o[k]);
      return o;
    });
  }
  return copy;
};

const parseJsonSafely = (value, fallback = {}) => {
  if (!value) return fallback;
  if (typeof value === "object") return value;
  if (typeof value !== "string") return fallback;
  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
};

const getLeaderboardPlayerName = (entry, index) => {
  const fullName = `${entry?.fname || ""} ${entry?.lname || ""}`.trim();
  return (
    fullName ||
    entry?.guest_name ||
    entry?.display_name ||
    entry?.username ||
    entry?.anonymous_id ||
    `Player ${index + 1}`
  );
};

const SpectatorQuiz = ({ broadcastSessionId }) => {
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [activeQuestions, setActiveQuestions] = useState([]);
  const [currentSong, setCurrentSong] = useState(null);
  const [error, setError] = useState(null);
  const sessionIdRef = useRef(null);

  const resolveSession = useCallback(async (hintSessionId) => {
    if (hintSessionId) {
      const res = await fetch(`${API_BASE}/api/live-quiz/sessions/${hintSessionId}`);
      if (res.ok) {
        const data = await res.json();
        const s = data.session || data;
        if (s?.session_id) return s;
      }
    }
    const activeRes = await fetch(`${API_BASE}/api/live-quiz/flash/active`);
    if (activeRes.ok) {
      const activeData = await activeRes.json();
      const sessions = activeData.sessions || [];
      if (sessions.length > 0) return sessions[0];
    }
    return null;
  }, []);

  const fetchLeaderboard = useCallback(async (sessionId) => {
    if (!sessionId) return;
    try {
      const res = await fetch(`${API_BASE}/api/live-quiz/sessions/${sessionId}/leaderboard`);
      if (res.ok) {
        const data = await res.json();
        setLeaderboard(data.leaderboard || []);
      }
    } catch (err) {
      console.error("[SpectatorQuiz] leaderboard error:", err);
    }
  }, []);

  const fetchCurrentQuestions = useCallback(async (sessionId) => {
    if (!sessionId) return;
    try {
      const res = await fetch(
        `${API_BASE}/api/live-quiz/flash/sessions/${sessionId}/current-questions`,
      );
      if (!res.ok) return;
      const data = await res.json();
      if (data.has_active_questions && Array.isArray(data.questions)) {
        setActiveQuestions(data.questions.map(stripAnswers));
        setCurrentSong(data.current_song || null);
      } else {
        setActiveQuestions([]);
        setCurrentSong(null);
      }
    } catch {
      // no active question
    }
  }, []);

  const bootstrap = useCallback(
    async (hintSessionId) => {
      try {
        const s = await resolveSession(hintSessionId);
        if (!s) {
          setSession(null);
          setError("No active Insta Quiz session");
          return;
        }
        setSession(s);
        sessionIdRef.current = s.session_id;
        setError(null);
        await Promise.all([
          fetchLeaderboard(s.session_id),
          fetchCurrentQuestions(s.session_id),
        ]);
      } catch (err) {
        console.error("[SpectatorQuiz] bootstrap error:", err);
        setError("Unable to load quiz");
      } finally {
        setLoading(false);
      }
    },
    [resolveSession, fetchLeaderboard, fetchCurrentQuestions],
  );

  useEffect(() => {
    bootstrap(broadcastSessionId || null);
  }, [bootstrap, broadcastSessionId]);

  useEffect(() => {
    const sessionId = session?.session_id;
    if (!sessionId) return;

    const socket = io(API_BASE, { transports: ["websocket"] });

    socket.on("connect", () => {
      socket.emit("flash:join", { sessionId });
      socket.emit("flash:join-global");
    });

    socket.on("flash:question_pushed", (payload) => {
      if (payload.session_id && payload.session_id !== sessionIdRef.current) return;
      const questions = (payload.questions || []).map(stripAnswers);
      setActiveQuestions(questions);
      setCurrentSong(payload.current_song || null);
      fetchLeaderboard(sessionId);
    });

    socket.on("flash:question_stopped", () => {
      setActiveQuestions([]);
      setCurrentSong(null);
      fetchLeaderboard(sessionId);
    });

    socket.on("flash:leaderboard", (payload) => {
      if (payload.session_id === sessionId && payload.leaderboard) {
        setLeaderboard(payload.leaderboard);
      }
    });

    socket.on("flash:quizlet_rank_updated", () => {
      fetchLeaderboard(sessionId);
    });

    socket.on("flash:session_ended", () => {
      setActiveQuestions([]);
      setCurrentSong(null);
      bootstrap(null);
    });

    socket.on("flash:activated", () => {
      bootstrap(sessionId);
    });

    const interval = setInterval(() => {
      fetchLeaderboard(sessionId);
    }, 10000);

    return () => {
      clearInterval(interval);
      socket.emit("flash:leave", { sessionId });
      socket.emit("flash:leave-global");
      socket.disconnect();
    };
  }, [session?.session_id, bootstrap, fetchLeaderboard]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh", bgcolor: "#0a0a0a" }}>
        <CircularProgress sx={{ color: "#9C27B0" }} />
      </Box>
    );
  }

  if (error && !session) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh", bgcolor: "#0a0a0a", px: 2 }}>
        <Typography sx={{ color: "rgba(255,255,255,0.5)", textAlign: "center" }}>{error}</Typography>
      </Box>
    );
  }

  const quizName = session?.quiz_name || "Insta Quiz";
  const rapidConfig = parseJsonSafely(session?.rapid_config, {});
  const totalQuestionTimeSeconds = Number(rapidConfig?.song_timer_seconds) || 60;
  const perQuestionSeconds =
    activeQuestions.length > 0
      ? Math.max(1, Math.round(totalQuestionTimeSeconds / activeQuestions.length))
      : totalQuestionTimeSeconds;

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#121210", p: { xs: 2, md: 4 } }}>
      <Box sx={{ textAlign: "center", mb: 3 }}>
        <Typography variant="h4" sx={{ color: "#fff", fontWeight: 800 }}>
          {quizName}
        </Typography>
        <Chip
          icon={<FlashIcon sx={{ color: "#FF9800 !important" }} />}
          label={session?.status === "active" ? "LIVE" : (session?.status || "").toUpperCase()}
          size="small"
          sx={{
            mt: 1,
            bgcolor: session?.status === "active" ? "rgba(0,200,83,0.15)" : "rgba(158,158,158,0.15)",
            color: session?.status === "active" ? "#00c853" : "#9e9e9e",
            border: `1px solid ${session?.status === "active" ? "#00c853" : "#9e9e9e"}`,
            fontWeight: 700,
          }}
        />
      </Box>

      {activeQuestions.length > 0 && (
        <Paper
          sx={{
            bgcolor: "#0b1120",
            border: "1px solid #9C27B0",
            borderRadius: 3,
            p: 3,
            mb: 3,
            maxWidth: 720,
            mx: "auto",
          }}
        >
          {currentSong && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2, justifyContent: "center" }}>
              <MusicIcon sx={{ color: "#4fc3f7" }} />
              <Typography sx={{ color: "#4fc3f7", fontWeight: 600 }}>
                {currentSong.name || currentSong.title || "Now playing"}
              </Typography>
            </Box>
          )}
          {activeQuestions.map((q, qi) => (
            <Box key={q.question_id || q.id || qi} sx={{ mb: qi < activeQuestions.length - 1 ? 3 : 0 }}>
              {(() => {
                const rawOptions = Array.isArray(q.options) ? q.options.slice(0, 4) : [];
                while (rawOptions.length < 4) rawOptions.push(`Option ${rawOptions.length + 1}`);
                return (
                  <>
              <Box sx={{ display: "flex", justifyContent: "center", mb: 1 }}>
                <Chip
                  label={`Question ${qi + 1} · ${perQuestionSeconds}s`}
                  size="small"
                  sx={{
                    bgcolor: "rgba(224,64,251,0.18)",
                    color: "#E040FB",
                    border: "1px solid rgba(224,64,251,0.4)",
                    fontWeight: 700,
                  }}
                />
              </Box>
              <Typography sx={{ color: "#fff", fontSize: "1.15rem", fontWeight: 600, mb: 2, textAlign: "center" }}>
                {q.question_text || q.question || `Question ${qi + 1}`}
              </Typography>
              <Grid container spacing={1.5}>
                {rawOptions.map((opt, i) => (
                  <Grid item xs={12} sm={6} key={i}>
                    <Box
                      sx={{
                        bgcolor: "rgba(255,255,255,0.05)",
                        border: "1px solid rgba(148,163,184,0.3)",
                        borderRadius: 2,
                        p: 1.5,
                        textAlign: "center",
                      }}
                    >
                      <Typography sx={{ color: "rgba(255,255,255,0.9)", fontSize: "0.95rem" }}>
                        {typeof opt === "string" ? opt : opt.text || opt.option_text || `Option ${i + 1}`}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
                  </>
                );
              })()}
            </Box>
          ))}
        </Paper>
      )}

      <Paper sx={{ bgcolor: "#020617", border: "1px solid #333", borderRadius: 3, p: 3, maxWidth: 720, mx: "auto" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
          <TrophyIcon sx={{ color: "#FFD700" }} />
          <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "1.1rem" }}>Leaderboard</Typography>
          <Chip label={`${leaderboard.length} players`} size="small" sx={{ ml: "auto", bgcolor: "rgba(255,255,255,0.08)", color: "#aaa" }} />
        </Box>
        <Divider sx={{ borderColor: "#333", mb: 2 }} />

        {leaderboard.length === 0 ? (
          <Typography sx={{ color: "rgba(255,255,255,0.4)", textAlign: "center", py: 4 }}>
            No participants yet...
          </Typography>
        ) : (
          <Box sx={{ maxHeight: 420, overflowY: "auto" }}>
            {leaderboard.slice(0, 25).map((entry, index) => (
              <Box
                key={entry.participant_id || index}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  py: 1.2,
                  px: 1,
                  borderRadius: 1,
                  bgcolor: index < 3 ? "rgba(255,215,0,0.05)" : "transparent",
                  mb: 0.5,
                }}
              >
                <Typography
                  sx={{
                    color: index === 0 ? "#FFD700" : index === 1 ? "#C0C0C0" : index === 2 ? "#CD7F32" : "#888",
                    fontWeight: 700,
                    width: 36,
                  }}
                >
                  #{index + 1}
                </Typography>
                <Typography sx={{ color: "#fff", flex: 1 }}>
                  {getLeaderboardPlayerName(entry, index)}
                </Typography>
                <Typography sx={{ color: "#E040FB", fontWeight: 700 }}>
                  {entry.total_score ?? entry.score ?? 0}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default SpectatorQuiz;
